import React from 'react';
import { Box, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MovieCreationOutlinedIcon from '@mui/icons-material/MovieCreationOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { midnight } from './midnightStyles';

const TIER_GOLD = '#FFD700';
const TIER_SILVER = '#C0C0C0';

function FlowArrow() {
    return <ArrowForwardIcon sx={{ fontSize: 16, color: midnight.onSurfaceVariant, opacity: 0.6, flexShrink: 0 }} />;
}

function DiagramNode({
    icon,
    label,
    sublabel,
    accent,
}: {
    icon: React.ReactNode;
    label: string;
    sublabel?: string;
    accent?: string;
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minWidth: 72,
                flex: 1,
            }}>
            <Box
                sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: accent ? `${accent}22` : midnight.surfaceContainer,
                    border: `1px solid ${accent ? `${accent}55` : 'rgba(255,255,255,0.08)'}`,
                    color: accent || midnight.primary,
                    mb: 0.75,
                }}>
                {icon}
            </Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: midnight.onSurface, lineHeight: 1.2 }}>
                {label}
            </Typography>
            {sublabel ? (
                <Typography sx={{ fontSize: 10, color: midnight.onSurfaceVariant, mt: 0.25 }}>{sublabel}</Typography>
            ) : null}
        </Box>
    );
}

export function ContestTiersAwardsHelpDiagram() {
    return (
        <Box
            sx={{
                mb: 3,
                p: 2,
                borderRadius: '12px',
                bgcolor: midnight.surfaceContainer,
                border: '1px solid rgba(255,255,255,0.06)',
            }}>
            <Typography
                sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: midnight.onSurfaceVariant,
                    mb: 2,
                }}>
                At a glance
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2.5, overflowX: 'auto', pb: 0.5 }}>
                <DiagramNode icon={<PersonOutlineIcon sx={{ fontSize: 22 }} />} label="Creator" sublabel="picks tier" />
                <FlowArrow />
                <DiagramNode
                    icon={<WorkspacePremiumOutlinedIcon sx={{ fontSize: 22, color: TIER_GOLD }} />}
                    label="Gold"
                    sublabel="$99 entry"
                    accent={TIER_GOLD}
                />
                <DiagramNode
                    icon={<WorkspacePremiumOutlinedIcon sx={{ fontSize: 22, color: TIER_SILVER }} />}
                    label="Silver"
                    sublabel="$49 entry"
                    accent={TIER_SILVER}
                />
                <FlowArrow />
                <DiagramNode
                    icon={<MovieCreationOutlinedIcon sx={{ fontSize: 22 }} />}
                    label="Submits"
                    sublabel="one entry"
                />
            </Box>

            <Box
                sx={{
                    borderRadius: '10px',
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.08)',
                    mb: 2,
                }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1.1fr 1fr 1fr',
                        bgcolor: midnight.surfaceHighest,
                        py: 1,
                        px: 1.5,
                        gap: 1,
                    }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: midnight.onSurfaceVariant }}>
                        Placement
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <WorkspacePremiumOutlinedIcon sx={{ fontSize: 14, color: TIER_GOLD }} />
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: TIER_GOLD }}>Gold prize</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <WorkspacePremiumOutlinedIcon sx={{ fontSize: 14, color: TIER_SILVER }} />
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: TIER_SILVER }}>Silver prize</Typography>
                    </Box>
                </Box>
                {[
                    { place: '1st Place', gold: '$600', silver: '$100' },
                    { place: '2nd Place', gold: '$300', silver: '$50' },
                ].map((row, i) => (
                    <Box
                        key={row.place}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1.1fr 1fr 1fr',
                            py: 1.25,
                            px: 1.5,
                            gap: 1,
                            bgcolor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
                            borderTop: '1px solid rgba(255,255,255,0.05)',
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <EmojiEventsOutlinedIcon sx={{ fontSize: 16, color: midnight.primary }} />
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: midnight.onSurface }}>
                                {row.place}
                            </Typography>
                        </Box>
                        <Typography
                            sx={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: midnight.primary,
                                textAlign: 'center',
                            }}>
                            {row.gold}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: midnight.onSurfaceVariant,
                                textAlign: 'center',
                            }}>
                            {row.silver}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: '10px',
                    bgcolor: 'rgba(255,201,101,0.06)',
                    border: '1px dashed rgba(255,201,101,0.25)',
                }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(255,201,101,0.12)',
                        flexShrink: 0,
                    }}>
                    <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 22, color: midnight.primary }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: midnight.onSurface }}>
                        Prize pool = sum of all cash prizes
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: midnight.onSurfaceVariant, mt: 0.25 }}>
                        Reserved from your campaign wallet when you publish
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
